import {
    Avatar,
    Box,
    Button,
    Divider,
    Flex,
    Grid,
    HStack,
    Icon,
    Image,
    Link,
    ModalCloseButton,
    Show,
    Skeleton,
    SkeletonCircle,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react'
import { type ContactPerson, PostData } from '@core/app/types/postsApiType'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import getAttachmentIcon from '@core/utils/helpers/getAttachmentIcon'
import getBrowser from '@core/utils/helpers/getBrowser'
import { prettifyPrice } from '@core/utils/helpers/prettifyPrice'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'
import { Map } from '@page/post/components/Map/Map'
import { Currencies } from '@page/logwork/helpers/currencies'

import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@chakra-ui/react'

import { useGetPostInfoQuery, useGetPostSettingsQuery, useSetJobApplyCandidateMutation } from '@core/app/api/postsApi'
import { setResetForm } from '@core/app/slices/profile'
import copyLink from '@core/assets/images/copy_link.svg'
import facebook from '@core/assets/images/facebook_colored.svg'
import linkedin from '@core/assets/images/linked_colored.svg'
import { AppButton } from '@core/components/AppButton'
import { RECAPTCHA } from '@core/utils/constants/constants'
import { useIsApplyDialog } from '@core/utils/contexts/ApplyDialogContext/ApplDialogContext'
import { ModalContext } from '@core/utils/contexts/ModalContext/ModalContext'
import { getDateWithShortMonthName } from '@core/utils/helpers/getDateWithShortMonthName'
import { useAppDispatch } from '@core/utils/hooks/hook'
import contactLinkedin from '@page/post/assets/images/contact_linkedin.svg'
import contactEmail from '@page/post/assets/images/contact_mail.svg'
import copyLinkWhite from '@page/post/assets/images/link_white.svg'
import phoneMobile from '@page/post/assets/images/mobile_phone.svg'
import phoneOffice from '@page/post/assets/images/office_phone.svg'
import postFacebook from '@page/post/assets/images/post_facebook.svg'
import postInstagram from '@page/post/assets/images/post_instagram.svg'
import postLinkedin from '@page/post/assets/images/post_linkedin.svg'
import postTwitter from '@page/post/assets/images/post_twitter.svg'
import dayjs from 'dayjs'
import PersonContactLink from '@page/post/components/AsideInfo/PersonContactLink'
import { useContext, useRef, useState, useEffect, useMemo } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useIsEmployee from '@core/utils/hooks/useIsEmployee'
import { Localization } from '@core/app/types/globalApiType'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import { useSearchParams } from 'react-router-dom'
import './AsideInfo.scss'

interface Props {
    data: PostData | undefined
    loaded: boolean
    changeView: (value: boolean) => void
    isPostExpired: boolean
}

const baseClass = 'PostPage__AsideInfo'

export const AsideInfo = ({ data, loaded, isPostExpired }: Props): JSX.Element => {
    const [localization, setLocalization] = useState<Localization>({})
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { id } = useParams()
    const [applyJob] = useSetJobApplyCandidateMutation()
    const { openModal, closeModal } = useContext(ModalContext)
    const { open, isApplyDialog, close } = useIsApplyDialog()
    const { isEmployee } = useIsEmployee()
    const { appTheme } = useAppTheme()
    const [contactPerson, setContactPerson] = useState<ContactPerson | null>(null)
    const [serviceCode] = useLocalStorageStore('service_code')
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])
    const { data: settings, isSuccess } = useGetPostSettingsQuery(
        {
            id: numberId,
            code: serviceCode,
        },
        { skip: !numberId }
    )
    const { data: postInfo } = useGetPostInfoQuery(
        {
            id: numberId,
        },
        { skip: !numberId }
    )
    const recaptchaRef = useRef<ReCAPTCHA>(null)
    const [showCopyLabel, setShowCopyLabel] = useState(false)
    const [applDialog] = useLocalStorageStore('apply_dialog')
    const [isMapModalOpen, setIsMapModalOpen] = useState(false)
    const [deadlineExpanded, setDeadlineExpanded] = useState(false)
    const [clampedDeadline, setClampedDeadline] = useState(false)
    const deadlineTextRef = useRef<HTMLDivElement | null>(null)
    const [searchParams] = useSearchParams()
    const { onOpenApply } = useApplyV2Context()
    const applyVersion = searchParams.get('apply')
    const currentJobId = applDialog?.id

    useEffect(() => {
        if (deadlineTextRef?.current) {
            const lineHeight = parseFloat(getComputedStyle(deadlineTextRef.current).lineHeight)
            const maxHeight = lineHeight * 2
            setClampedDeadline(deadlineTextRef?.current?.scrollHeight > maxHeight)
        }
    }, [data])

    const openMapModal = () => {
        setIsMapModalOpen(true)
    }

    const closeMapModal = () => {
        setIsMapModalOpen(false)
    }

    const salaryCurrencyObject = useMemo(() => {
        if (data?.salaryData?.CurrencyID) {
            return Currencies.find((curr) => curr.id === data.salaryData?.CurrencyID)
        }
        return null
    }, [data])

    const submitHandler = async () => {
        recaptchaRef.current?.reset()
        let token = await recaptchaRef.current?.executeAsync()
        if (id && token) {
            try {
                await applyJob({
                    id,
                    data: {
                        'g-recaptcha-response': token,
                    },
                }).unwrap()

                close()
                localStorage.setItem('after_apply', '1')
                localStorage.setItem('after_apply_id', id)
                localStorage.setItem('after_apply_title', postInfo?.data?.ad?.Name || '')
                location.href = '/profile'
            } catch (error) {
            } finally {
            }
        }
    }

    useEffect(() => {
        if (!appTheme) return
        if (appTheme && appTheme?.localization) {
            setLocalization(appTheme?.localization)
        }
    }, [appTheme])

    const browser = getBrowser()

    const openApplyWindowV2 = () => {
        onOpenApply()
    }

    const openApplyWindowHandler = () => {
        if (settings?.externalLink && settings?.externalLink?.length > 0) {
            const externalLink = settings?.externalLink
            if (externalLink.startsWith('mailto:')) window.open(settings?.externalLink, '_blank')
            else {
                const link = settings?.externalLink.includes('http')
                    ? settings?.externalLink
                    : `https://${settings?.externalLink}`
                window.open(link, '_blank')?.focus()
            }
            return
        }

        const isNewApplyEnabled =
            postInfo?.data?.isNewApplyEnabled ??
            (document?.querySelector('input[name="__new_apply_form_enabled"]') as HTMLInputElement)?.value === '1'

        // TODO -start- Apply v2
        if (applyVersion === 'v2' || isNewApplyEnabled) {
            openApplyWindowV2()
            return
        }
        // TODO -end- Apply v2

        if (
            !settings?.externalAuth &&
            !settings?.askAttributes &&
            settings?.screeningQuestions?.length === 0 &&
            !settings?.settings?.anonymousApplication &&
            !settings?.fields?.applicationText.visible &&
            !settings?.fields?.address.visible &&
            !settings?.missingProfileFields?.address &&
            !settings?.fields?.attachments.visible &&
            !settings?.missingProfileFields?.attachments &&
            !settings?.fields?.cv.visible &&
            !settings?.missingProfileFields?.cv &&
            !settings?.fields?.dateOfBirth.visible &&
            !settings?.missingProfileFields?.dateOfBirth &&
            !settings?.fields?.gender.visible &&
            !settings?.missingProfileFields?.gender &&
            !settings?.fields?.linkedinUrl.visible &&
            !settings?.missingProfileFields?.linkedinUrl &&
            !settings?.fields?.mobilePhone.visible &&
            !settings?.missingProfileFields?.mobilePhone &&
            settings?.isUser
        ) {
            submitHandler()
            return
        }

        if (isApplyDialog) {
            if (id !== currentJobId) {
                openModal({
                    size: 'md',
                    isHeaderVisible: false,
                    isFooterVisible: false,
                    body: (
                        <Box p={6}>
                            <Text fontSize={'2xl'} fontWeight={'semibold'} color={'text.main'}>
                                {t('7399')}
                            </Text>
                            <Text color={'text.secondary'} mt={4}>
                                {t('7400')} {t('7401')},{' '}
                                <Text display={'inline'} fontWeight={'semibold'}>
                                    {t('7402')}
                                </Text>
                            </Text>
                            <Text color={'text.secondary'} mt={4}>
                                {t('7403')}
                            </Text>
                            <Flex
                                w={'full'}
                                flexDirection={'column'}
                                gap={3}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                mt={'40px'}
                            >
                                <Button
                                    variant={'error'}
                                    fontSize={'sm'}
                                    w={'full'}
                                    ra-data-job-post-id={id}
                                    id={'ra-button-job-post-action-application-form-close-ok'}
                                    onClick={() => {
                                        dispatch(setResetForm(true))

                                        setTimeout(() => {
                                            closeModal()
                                            localStorage.removeItem('mobile_verification')
                                        }, 0)

                                        open(id ? id : '', {
                                            title: decodeHtmlEntitiesRecursive(data?.ad?.JobTitle),
                                            link: `/job/${id}`,
                                            expiredDate: data?.ad?.Expire || '',
                                        })
                                    }}
                                >
                                    {t('7404')}
                                </Button>
                                <Button w={'full'} variant={'secondary'} onClick={closeModal}>
                                    {t('7405')}
                                </Button>
                            </Flex>
                        </Box>
                    ),
                })
            } else {
                open(id ? id : '', {
                    title: decodeHtmlEntitiesRecursive(data?.ad?.JobTitle),
                    link: `/job/${id}`,
                    expiredDate: data?.ad?.Expire || '',
                })
            }
        } else {
            open(id ? id : '', {
                title: decodeHtmlEntitiesRecursive(data?.ad?.JobTitle),
                link: `/job/${id}`,
                expiredDate: data?.ad?.Expire || '',
            })
        }
    }

    const copyToClipBoard = async (copyMe: string) => {
        try {
            setShowCopyLabel(true)
            await navigator.clipboard.writeText(copyMe)

            setTimeout(() => {
                setShowCopyLabel(false)
            }, 2000)
        } catch (err) {}
    }

    const getDifference = (expireDate: string) => {
        const date1 = dayjs(expireDate).startOf('day')
        const date2 = dayjs().startOf('day')

        const diffInMs = Math.abs(date1.diff(date2, 'millisecond'))
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInDays === 0) {
            return `(${t('152')})`.toLowerCase()
        } else if (diffInDays === 1) {
            return `(${t('153')})`.toLowerCase()
        } else if (diffInDays < 0) {
            return ''
        } else {
            return `(${diffInDays} ${t('1039').toLowerCase()})`
        }
    }

    function formatURL(url: string) {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            return `https://${url}`
        }
        return url
    }

    return (
        <>
            {/*<Box w={'full'} bg={'bg.1'} borderRadius={'8px'} boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'} mb={'16px'}>*/}
            <Box w={'full'} className={baseClass}>
                <Flex flexDirection={'column'}>
                    <VStack
                        px={6}
                        py={5}
                        gap={4}
                        alignItems={'start'}
                        bg={'bg.1'}
                        borderRadius={'8px'}
                        boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                        mb={'16px'}
                    >
                        <Text
                            fontSize={'xl'}
                            fontWeight={'semibold'}
                            color={'text.main'}
                            opacity={isPostExpired ? 0.4 : 1}
                        >
                            {t('1108')}
                        </Text>
                        {loaded ? (
                            <VStack w={'full'} gap={3}>
                                {data?.ad?.JobTitle && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('171')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {decodeHtmlEntitiesRecursive(data?.ad?.JobTitle)}
                                        </Text>
                                    </VStack>
                                )}
                                {data?.ad?.EmpName && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('713')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {decodeHtmlEntitiesRecursive(data?.ad?.EmpName || '')}
                                        </Text>
                                    </VStack>
                                )}
                                {data?.ad?.PlaceCountry || data?.ad?.PlaceCity || data?.ad?.PlaceRegion ? (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('1249').trim()}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {`${data?.ad?.PlaceCountry ? `${data?.ad?.PlaceCountry}` : ''}${
                                                data?.ad?.PlaceRegion ? `, ${data?.ad?.PlaceRegion}` : ''
                                            }${data?.ad?.PlaceCity ? `, ${data?.ad?.PlaceCity}` : ''}`}
                                        </Text>
                                    </VStack>
                                ) : null}
                                {data?.ad?.Place && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('1109')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {decodeHtmlEntitiesRecursive(data?.ad?.Place || '')}
                                        </Text>
                                    </VStack>
                                )}

                                {/* Work Location */}
                                {data?.ad?.EmploymentType && data?.ad?.EmploymentType > 0 && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('8135')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {(() => {
                                                switch (data?.ad?.EmploymentType) {
                                                    case 1:
                                                        return t('5546')
                                                    case 2:
                                                        return t('5547')
                                                    case 3:
                                                        return t('8138')
                                                    default:
                                                        return t('Not specified')
                                                }
                                            })()}
                                        </Text>
                                    </VStack>
                                )}

                                {/* Work Type */}
                                {data?.ad?.WorkType && data?.ad?.WorkType > 0 && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('357')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {(() => {
                                                switch (data?.ad?.WorkType) {
                                                    case 1:
                                                        return t('8139')
                                                    case 2:
                                                        return t('8263')
                                                    case 3:
                                                        return t('8140') //From home
                                                    default:
                                                        return t('Not specified')
                                                }
                                            })()}
                                        </Text>
                                    </VStack>
                                )}

                                {data?.salaryData && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('329')}:
                                        </Text>
                                        <Flex alignItems={'center'} gap={'4px'}>
                                            {data?.salaryData?.RangeType !== 3 && (
                                                <>
                                                    {salaryCurrencyObject?.img && (
                                                        <Image
                                                            maxW={'14px'}
                                                            src={`${process.env.ENV === 'local' ? process.env.API_URL : window.location.origin}${salaryCurrencyObject.img}`}
                                                            alt={salaryCurrencyObject?.title || 'Currency'}
                                                            filter={isPostExpired ? 'saturate(0) opacity(0.7)' : ''}
                                                        />
                                                    )}
                                                    <Text
                                                        fontSize={'sm'}
                                                        fontWeight={'medium'}
                                                        color={'text.main'}
                                                        opacity={isPostExpired ? 0.4 : 1}
                                                    >
                                                        {salaryCurrencyObject?.short}
                                                    </Text>
                                                </>
                                            )}
                                            <Text
                                                fontSize={'sm'}
                                                fontWeight={'medium'}
                                                color={'text.main'}
                                                opacity={isPostExpired ? 0.4 : 1}
                                            >
                                                {(() => {
                                                    const { PeriodType, RangeType, Amount, ToAmount } = data.salaryData

                                                    const getPeriodType = () => {
                                                        switch (PeriodType) {
                                                            case 1:
                                                                return t('4912')
                                                            case 2:
                                                                return t('753')
                                                            case 3:
                                                                return t('754')
                                                            case 4:
                                                                return t('931')
                                                            case 5:
                                                                return t('1322')
                                                            default:
                                                                return ''
                                                        }
                                                    }

                                                    switch (RangeType) {
                                                        case 1:
                                                            return `${prettifyPrice(Amount)} / ${getPeriodType()}`
                                                        case 2:
                                                            return `${prettifyPrice(Amount)} - ${prettifyPrice(ToAmount)} / ${getPeriodType()}`
                                                        case 3:
                                                            return prettifyPrice(data.ad.PositionSalary) || t('Custom')
                                                        default:
                                                            return t('Not specified')
                                                    }
                                                })()}
                                            </Text>
                                        </Flex>
                                    </VStack>
                                )}

                                {/* Fallback Salary Text */}
                                {!data?.salaryData && data?.ad?.PositionSalary && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('329')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            fontWeight={'semibold'}
                                            color={'text.main'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {decodeHtmlEntitiesRecursive(data.ad.PositionSalary || t('Not specified'))}
                                        </Text>
                                    </VStack>
                                )}

                                {data?.ad?.Webpage && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('215')}:
                                        </Text>
                                        <Link
                                            color={'text.link'}
                                            fontSize={'sm'}
                                            fontWeight={'semibold'}
                                            href={
                                                data?.ad?.Webpage.includes('https://')
                                                    ? `${data?.ad?.Webpage}`
                                                    : `https://${data?.ad?.Webpage}`
                                            }
                                            rel={'noopener noreferrer'}
                                            wordBreak={'break-word'}
                                            target={'_blank'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {decodeHtmlEntitiesRecursive(data?.ad?.Webpage || '')}
                                        </Link>
                                    </VStack>
                                )}
                                {data?.ad?.Sector && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('330')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {data?.ad?.Sector}
                                        </Text>
                                    </VStack>
                                )}
                                {data?.ad?.Extent && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {localization[15] ? localization[15] : t('331')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {data?.ad?.Extent}
                                        </Text>
                                    </VStack>
                                )}
                                {data?.ad?.Positions && data.ad?.Positions > 1 ? (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {(() => {
                                                console.debug('Localization at index 11:', localization[11])
                                                if (localization[11]) {
                                                    const localizedText = `${localization[11]}`
                                                    const capitalizedText =
                                                        localizedText.charAt(0).toUpperCase() + localizedText.slice(1)
                                                    // console.log("Localization[11] exists, value:", capitalizedText);
                                                    return capitalizedText
                                                } else if (data?.ad?.Positions === 1) {
                                                    // console.log("Positions === 1, using t('5369'):", t('5369'));
                                                    return ` ${t('5369')}`
                                                } else {
                                                    // console.log("Positions !== 1, using t('2413'):", t('2413'));
                                                    return ` ${t('2413')}`
                                                }
                                            })()}
                                            :
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {data?.ad?.Positions}
                                        </Text>
                                    </VStack>
                                ) : null}
                                {data?.ad?.Expire && data?.ad?.ShowEndDate && !data?.ad?.Deadline && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {localization[24] ? localization[24] : t('6632')}:
                                        </Text>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.main'}
                                            fontWeight={'semibold'}
                                            wordBreak={'break-word'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {getDateWithShortMonthName(data?.ad?.Expire, t)}{' '}
                                            {getDifference(data?.ad?.Expire)}
                                        </Text>
                                    </VStack>
                                )}
                                {data?.ad?.Deadline && (
                                    <VStack gap={1} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('334')}:
                                        </Text>
                                        <Tooltip
                                            label={data?.ad?.Deadline}
                                            hasArrow
                                            bg={'#323234'}
                                            borderRadius={'4px'}
                                            px={'16px'}
                                            py={'8px'}
                                            fontSize={'xs'}
                                            placement={'bottom'}
                                            closeOnScroll
                                            shouldWrapChildren
                                            isDisabled={deadlineExpanded}
                                        >
                                            <Text
                                                ref={deadlineTextRef}
                                                fontSize={'sm'}
                                                color={'text.main'}
                                                fontWeight={'semibold'}
                                                wordBreak={'break-word'}
                                                opacity={isPostExpired ? 0.4 : 1}
                                                className={`${baseClass}--deadline-text ${!deadlineExpanded ? 'expanded' : ''}`}
                                            >
                                                {data?.ad?.Deadline}
                                            </Text>
                                        </Tooltip>
                                        {clampedDeadline && (
                                            <Button
                                                m={0}
                                                p={0}
                                                height={'auto'}
                                                border={'none'}
                                                background={'none'}
                                                // textColor={'#6EA3EA'}
                                                // w={'auto'}
                                                // mx={'auto'}
                                                _hover={{
                                                    backgroundColor: 'none',
                                                }}
                                                _active={{
                                                    backgroundColor: 'none',
                                                }}
                                                rightIcon={
                                                    <Box
                                                        style={{
                                                            transform: !deadlineExpanded ? 'none' : 'rotate(180deg)',
                                                        }}
                                                    >
                                                        <svg
                                                            viewBox={`0 0 8 2`}
                                                            width={'8px'}
                                                            height={'8px'}
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M1 0.5L4 3.5L7 0.5"
                                                                stroke={'#6EA3EA'}
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </Box>
                                                }
                                                // variant={'outline'}
                                                onClick={() => setDeadlineExpanded((prev) => !prev)}
                                                color={'#6EA3EA'}
                                                // borderColor={'#E8E8E8'}
                                                // fontWeight={'medium'}
                                                // fontSize={'sm'}
                                            >
                                                {!deadlineExpanded ? t('6810') : t('7423')}
                                            </Button>
                                        )}
                                    </VStack>
                                )}
                                {data?.ad?.Twitter ||
                                data?.ad?.Facebook ||
                                data?.ad?.Linkedin ||
                                data?.ad?.Instagram ? (
                                    <VStack gap={2} w={'full'} alignItems={'start'}>
                                        <Text
                                            fontSize={'sm'}
                                            color={'text.secondary'}
                                            minW={'150px'}
                                            opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {t('2494')}:
                                        </Text>
                                        <HStack gap={3} alignItems={'flex-start'}>
                                            {data?.ad?.Facebook && (
                                                <Link
                                                    href={formatURL(data?.ad?.Facebook)}
                                                    target={'_blank'}
                                                    aria-label={'Facebook'}
                                                    opacity={isPostExpired ? 0.4 : 1}
                                                >
                                                    <HStack gap={2}>
                                                        <Image
                                                            src={postFacebook}
                                                            w={'20px'}
                                                            alt={'Facebook'}
                                                            filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </HStack>
                                                </Link>
                                            )}
                                            {data?.ad?.Twitter && (
                                                <Link
                                                    href={formatURL(data?.ad?.Twitter)}
                                                    target={'_blank'}
                                                    aria-label={'Twitter'}
                                                    opacity={isPostExpired ? 0.4 : 1}
                                                >
                                                    <HStack gap={2}>
                                                        <Image
                                                            src={postTwitter}
                                                            w={'20px'}
                                                            alt={'Twitter'}
                                                            filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </HStack>
                                                </Link>
                                            )}
                                            {data?.ad?.Linkedin && (
                                                <Link
                                                    href={formatURL(data?.ad?.Linkedin)}
                                                    target={'_blank'}
                                                    aria-label={'LinkedIn'}
                                                    opacity={isPostExpired ? 0.4 : 1}
                                                >
                                                    <HStack gap={2}>
                                                        <Image
                                                            src={postLinkedin}
                                                            w={'20px'}
                                                            alt={'LinkedIn'}
                                                            filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </HStack>
                                                </Link>
                                            )}
                                            {data?.ad?.Instagram && (
                                                <Link
                                                    href={formatURL(data?.ad?.Instagram)}
                                                    target={'_blank'}
                                                    aria-label={'Instagram'}
                                                    opacity={isPostExpired ? 0.4 : 1}
                                                >
                                                    <HStack gap={2}>
                                                        <Image
                                                            src={postInstagram}
                                                            w={'20px'}
                                                            alt={'Instagram'}
                                                            filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </HStack>
                                                </Link>
                                            )}
                                        </HStack>
                                    </VStack>
                                ) : null}
                            </VStack>
                        ) : (
                            <VStack gap={2} w={'full'}>
                                {Array.from(Array(8).keys()).map((_, index) => (
                                    <Skeleton w={'full'} h={'21px'} key={index} />
                                ))}
                            </VStack>
                        )}
                        {isSuccess &&
                            settings?.application &&
                            Object.keys(settings?.application || {}).length === 0 && (
                                <Show above={'xl'}>
                                    {id && (
                                        <AppButton
                                            translate="no"
                                            title={t('6634', 'Apply now')
                                                .toLowerCase()
                                                .replace(/^\w/, (c) => c.toUpperCase())}
                                            variant={'primary'}
                                            w={'full'}
                                            ra-data-job-post-id={id}
                                            id={'ra-button-job-post-action-apply-now'}
                                            onClick={() => openApplyWindowHandler()}
                                            isDisabled={isPostExpired}
                                        />
                                    )}
                                </Show>
                            )}
                    </VStack>
                    {(data?.contactPersons || []).length > 0 && (
                        <VStack
                            px={6}
                            py={5}
                            gap={4}
                            alignItems={'start'}
                            bg={'bg.1'}
                            borderRadius={'8px'}
                            boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                            mb={'16px'}
                        >
                            <Text
                                fontSize={'xl'}
                                fontWeight={'semibold'}
                                color={'text.main'}
                                opacity={isPostExpired ? 0.4 : 1}
                            >
                                {t('217')}
                            </Text>
                            <VStack w={'full'} alignItems={'start'}>
                                {(data?.contactPersons || []).map((contact) => (
                                    <Box key={contact.AdContactPersonID}>
                                        <HStack alignItems={'start'} gap={6}>
                                            <Avatar
                                                name={contact.ContactName}
                                                src={contact.Image}
                                                w={'80px'}
                                                h={'80px'}
                                                filter={isPostExpired ? 'saturate(0) opacity(0.7)' : ''}
                                            />
                                            <Flex flexDirection={'column'} alignItems={'start'}>
                                                <Text
                                                    wordBreak={'break-word'}
                                                    fontSize={'sm'}
                                                    fontWeight={'semibold'}
                                                    color={'text.main'}
                                                    opacity={isPostExpired ? 0.4 : 1}
                                                >
                                                    {contact.ContactName}
                                                </Text>
                                                <Text
                                                    mt={1}
                                                    wordBreak={'break-word'}
                                                    fontSize={'sm'}
                                                    color={'text.secondary'}
                                                    opacity={isPostExpired ? 0.4 : 1}
                                                >
                                                    {contact.Title}
                                                </Text>
                                                <Link
                                                    onClick={() => {
                                                        setContactPerson(contact)
                                                    }}
                                                    mt={'4px'}
                                                    lineHeight={'20px'}
                                                    fontSize={'12px'}
                                                    color={'#2F80ED'}
                                                    _hover={{ textDecoration: 'none', fontWeight: 600 }}
                                                >
                                                    {t('8753')}
                                                </Link>
                                                {contactPerson && (
                                                    <Modal
                                                        isOpen={!!contactPerson}
                                                        onClose={() => setContactPerson(null)}
                                                        size={'md'}
                                                    >
                                                        <ModalOverlay backgroundColor={'rgba(0,0,0,0.2)'} />
                                                        <ModalContent
                                                            className={'ContactPerson__content'}
                                                            maxWidth={'432px'}
                                                        >
                                                            <ModalHeader
                                                                pt={'48px'}
                                                                px={'48px'}
                                                                pb={'32px'}
                                                                fontSize={'24px'}
                                                                color={'#1A1A1A'}
                                                            >
                                                                {t('8755')}
                                                            </ModalHeader>
                                                            <ModalCloseButton top={'50px'} right={'22px'} />
                                                            <ModalBody px={'48px'}>
                                                                <Grid
                                                                    templateColumns={'auto minmax(100px, 1fr)'}
                                                                    overflow={'hidden'}
                                                                    gap={'24px'}
                                                                    rowGap={'12px'}
                                                                    alignItems={'center'}
                                                                >
                                                                    <Avatar
                                                                        w={'80px'}
                                                                        h={'80px'}
                                                                        src={contactPerson?.Image}
                                                                    />
                                                                    <VStack alignItems={'start'} gap={0}>
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'semibold'}
                                                                            color={'#1A1A1A'}
                                                                        >
                                                                            {contactPerson?.ContactName}
                                                                        </Text>
                                                                        {contactPerson?.Title && (
                                                                            <Text
                                                                                fontSize={'sm'}
                                                                                color={'text.secondary'}
                                                                                lineHeight={'20px'}
                                                                            >
                                                                                {contactPerson.Title}
                                                                            </Text>
                                                                        )}
                                                                        <Flex>
                                                                            {contactPerson?.Linkedin && (
                                                                                <Link
                                                                                    href={formatURL(
                                                                                        contactPerson?.Linkedin
                                                                                    )}
                                                                                    target={'_blank'}
                                                                                    aria-label={'LinkedIn'}
                                                                                    opacity={isPostExpired ? 0.4 : 1}
                                                                                >
                                                                                    <HStack gap={2} mt={'8px'}>
                                                                                        <Image
                                                                                            src={contactLinkedin}
                                                                                            w={'20px'}
                                                                                            alt={'LinkedIn'}
                                                                                            filter={
                                                                                                isPostExpired
                                                                                                    ? 'saturate(0) opacity(0.7)'
                                                                                                    : 'none'
                                                                                            }
                                                                                        />
                                                                                    </HStack>
                                                                                </Link>
                                                                            )}
                                                                        </Flex>
                                                                    </VStack>
                                                                    <VStack
                                                                        alignItems={'start'}
                                                                        gap={'4px'}
                                                                        rowGap={'8px'}
                                                                    >
                                                                        {contactPerson?.WorkPhone && (
                                                                            <Text
                                                                                display={'flex'}
                                                                                gap={'8px'}
                                                                                fontSize={'sm'}
                                                                            >
                                                                                <i className="fa-solid fa-phone ContactPerson__modal__icon" />
                                                                                {t('2415')}:
                                                                            </Text>
                                                                        )}
                                                                        {contactPerson?.MobilePhone && (
                                                                            <Text
                                                                                display={'flex'}
                                                                                gap={'8px'}
                                                                                fontSize={'sm'}
                                                                            >
                                                                                <i className="fa-solid fa-mobile ContactPerson__modal__icon" />
                                                                                {t('2972')}:
                                                                            </Text>
                                                                        )}
                                                                        {contactPerson?.Email && (
                                                                            <Text
                                                                                display={'flex'}
                                                                                gap={'8px'}
                                                                                fontSize={'sm'}
                                                                            >
                                                                                <i className="fa-solid fa-envelope ContactPerson__modal__icon" />
                                                                                {t('92')}:
                                                                            </Text>
                                                                        )}
                                                                    </VStack>
                                                                    <VStack alignItems={'start'} gap={'4px'}>
                                                                        {contactPerson?.WorkPhone && (
                                                                            <PersonContactLink
                                                                                type={'tel'}
                                                                                value={contactPerson.WorkPhone}
                                                                                phonePrefix={
                                                                                    contactPerson.WorkPhonePrefix
                                                                                }
                                                                            />
                                                                        )}
                                                                        {contactPerson?.MobilePhone && (
                                                                            <PersonContactLink
                                                                                type={'tel'}
                                                                                value={contactPerson.MobilePhone}
                                                                                phonePrefix={
                                                                                    contactPerson.MobilePhonePrefix
                                                                                }
                                                                            />
                                                                        )}
                                                                        {contactPerson?.Email && (
                                                                            <PersonContactLink
                                                                                type={'mailto'}
                                                                                value={contactPerson.Email}
                                                                            />
                                                                        )}
                                                                    </VStack>
                                                                </Grid>
                                                            </ModalBody>
                                                            <ModalFooter pb={'48px'} px={'48px'} pt={'32px'}>
                                                                <Button
                                                                    colorScheme="blue"
                                                                    variant="outline"
                                                                    width={'full'}
                                                                    onClick={() => setContactPerson(null)}
                                                                    _hover={{ bg: 'blue.500', color: 'white' }}
                                                                >
                                                                    {t('3723')}
                                                                </Button>
                                                            </ModalFooter>
                                                        </ModalContent>
                                                    </Modal>
                                                )}
                                            </Flex>
                                        </HStack>
                                    </Box>
                                ))}
                            </VStack>
                        </VStack>
                    )}
                    {data?.ad?.ShowLocation && data?.locations && data?.locations.length > 0 ? (
                        <VStack
                            px={6}
                            py={5}
                            gap={2}
                            alignItems={'start'}
                            bg={'bg.1'}
                            borderRadius={'8px'}
                            boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                            mb={'16px'}
                        >
                            <Text
                                fontSize={'xl'}
                                fontWeight={'semibold'}
                                color={'text.main'}
                                opacity={isPostExpired ? 0.4 : 1}
                            >
                                {t('6477')}
                            </Text>
                            <Skeleton isLoaded={loaded}>
                                {/* {data?.ad?.PlaceCountry && data?.ad?.PlaceCity && (
                                    <Text fontSize={'sm'} color={'text.secondary'} opacity={isPostExpired ? 0.4 : 1}>
                                        {`${data?.ad?.FirstAddress ? data?.ad?.FirstAddress : ''} ${data?.ad?.PlaceCity ? data?.ad?.PlaceCity : ''
                                            } ${data?.ad?.PlaceRegion ? data?.ad?.PlaceRegion : ''} ${data?.ad?.PlaceCountry ? data?.ad?.PlaceCountry : ''
                                            }`}

                                    </Text>
                                )} */}

                                {data?.locations?.map((location, index) => (
                                    <Text fontSize={'sm'} color={'text.secondary'} opacity={isPostExpired ? 0.4 : 1}>
                                        {location.FormattedAddress}
                                    </Text>
                                ))}
                            </Skeleton>

                            {!(browser.name === 'Safari' && browser.version > 15.3) && (
                                <AppButton
                                    mt={'20px'}
                                    translate="no"
                                    variant={'outline'}
                                    title={t('6222')}
                                    ra-data-job-post-id={id}
                                    id={'ra-button-job-post-action-apply-now'}
                                    w={'full'}
                                    onClick={openMapModal}
                                    opacity={isPostExpired ? 0.4 : 1}
                                />
                            )}

                            <Modal isOpen={isMapModalOpen} onClose={closeMapModal} size="xl">
                                <ModalOverlay />
                                <ModalContent maxW="80vw" width="80vw" maxH="80vh" height="80vh">
                                    <ModalHeader mt={'20px'} pb={'0px'}>
                                        {t('6477')}
                                    </ModalHeader>
                                    <ModalBody>
                                        {/* {data?.ad?.PlaceCountry && data?.ad?.PlaceCity && (
                                            <Text pb={'20px'} fontSize={'sm'} color={'text.secondary'}>
                                                {`${data?.ad?.FirstAddress ? data?.ad?.FirstAddress : ''} ${data?.ad?.PlaceCity ? data?.ad?.PlaceCity : ''
                                                    } ${data?.ad?.PlaceRegion ? data?.ad?.PlaceRegion : ''} ${data?.ad?.PlaceCountry ? data?.ad?.PlaceCountry : ''
                                                    }`}
                                            </Text>
                                        )} */}

                                        {data?.locations?.map((location, index) => (
                                            <Text
                                                key={index}
                                                pb={index === data.locations.length - 1 ? '20px' : '0'}
                                                fontSize="sm"
                                                color="text.secondary"
                                            >
                                                {location.FormattedAddress}
                                            </Text>
                                        ))}

                                        {isMapModalOpen && (
                                            // <Map
                                            //     data={
                                            //         data?.locations?.length > 0
                                            //             ? [
                                            //                 ...data?.locations,
                                            //                 {
                                            //                     FormattedAddress: `${data?.ad?.PlaceCountry || ''}, ${data?.ad?.PlaceCity || ''}, ${data?.ad?.FirstAddress || ''}`,
                                            //                     Lat: '',
                                            //                     Lng: '',
                                            //                     PlaceID: '',
                                            //                 },
                                            //             ]
                                            //             : data?.ad?.PlaceCountry && data?.ad?.PlaceCity
                                            //                 ? `${data?.ad?.PlaceCountry}, ${data?.ad?.PlaceCity}, ${data?.ad?.FirstAddress || ''}`
                                            //                 : ''
                                            //     }
                                            // />
                                            <Map data={data?.locations || []} />
                                        )}
                                    </ModalBody>
                                    <ModalFooter>
                                        {/* Close Map Button */}
                                        <AppButton
                                            pt={'0px'}
                                            mb={'20px'}
                                            translate="no"
                                            variant={'outline'}
                                            title={t('8386')}
                                            ra-data-job-post-id={id}
                                            id={'ra-button-job-post-action-apply-now'}
                                            w={'full'}
                                            onClick={closeMapModal}
                                        />
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>

                            {/* <Skeleton isLoaded={loaded} w={'full'} h={'225px'}>
                                <Map
                                    data={
                                        data?.locations?.length > 0
                                            ? [
                                                ...data?.locations,
                                                {
                                                    FormattedAddress: `${data?.ad?.PlaceCountry}, ${data?.ad?.PlaceCity}, ${data?.ad?.FirstAddress}`,
                                                    Lat: '',
                                                    Lng: '',
                                                    PlaceID: ''
                                                }
                                            ]
                                            : `${data?.ad?.PlaceCountry}, ${data?.ad?.PlaceCity}, ${data?.ad?.FirstAddress}`
                                    }
                                />
                            </Skeleton> */}
                        </VStack>
                    ) : null}

                    <VStack
                        px={6}
                        py={5}
                        gap={2}
                        alignItems={'start'}
                        bg={'bg.1'}
                        borderRadius={'8px'}
                        boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                        mb={'16px'}
                    >
                        <Text
                            fontSize={'xl'}
                            fontWeight={'semibold'}
                            color={'text.main'}
                            opacity={isPostExpired ? 0.4 : 1}
                        >
                            {t('2914')}
                        </Text>
                        <Text fontSize={'sm'} color={'text.secondary'} opacity={isPostExpired ? 0.4 : 1}>
                            {t('6476')}
                        </Text>
                        <HStack gap={4}>
                            {data?.shareLinks?.facebook && (
                                <Link
                                    href={`https://www.facebook.com/sharer.php?u=${encodeURIComponent(
                                        data?.shareLinks?.facebook
                                    )}`}
                                    target={'_blank'}
                                    aria-label="Facebook"
                                    opacity={isPostExpired ? 0.4 : 1}
                                >
                                    <Image
                                        src={facebook}
                                        alt="Facebook"
                                        filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                    />
                                </Link>
                            )}
                            {data?.shareLinks?.twitter && (
                                <Link
                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                                        data?.shareLinks?.twitter
                                    )}`}
                                    target={'_blank'}
                                    aria-label="Twitter"
                                    opacity={isPostExpired ? 0.4 : 1}
                                >
                                    <Image
                                        src={postTwitter}
                                        alt="Twitter"
                                        filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                    />
                                </Link>
                            )}
                            {data?.shareLinks?.linkedin && (
                                <Link
                                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                                        data?.shareLinks?.linkedin
                                    )}&source=recman`}
                                    target={'_blank'}
                                    aria-label="LinkedIn"
                                    opacity={isPostExpired ? 0.4 : 1}
                                >
                                    <Image
                                        src={linkedin}
                                        alt="LinkedIn"
                                        filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                    />
                                </Link>
                            )}

                            {data?.shareLinks?.self && (
                                <Tooltip
                                    isOpen={showCopyLabel}
                                    bg={'#212328'}
                                    boxShadow={'0px 16px 40px 0px rgba(0, 0, 0, 0.08)'}
                                    borderRadius={'4px'}
                                    placement={'bottom'}
                                    label={
                                        <HStack px={4} py={'10px'}>
                                            <Image
                                                src={copyLinkWhite}
                                                maxW={4}
                                                alt={`${t('5708')}`}
                                                filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                            />
                                            <Text
                                                fontSize={'sm'}
                                                color={'text.white'}
                                                fontWeight={'medium'}
                                                opacity={isPostExpired ? 0.4 : 1}
                                            >
                                                {t('7107')}
                                            </Text>
                                        </HStack>
                                    }
                                >
                                    <Button
                                        onClick={() => copyToClipBoard(data?.shareLinks?.self)}
                                        p={0}
                                        aria-label={`${t('5708')}`}
                                        opacity={isPostExpired ? 0.4 : 1}
                                    >
                                        <Image
                                            src={copyLink}
                                            alt={`${t('5708')}`}
                                            filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                        />
                                    </Button>
                                </Tooltip>
                            )}
                        </HStack>
                    </VStack>
                    {data?.files && data?.files.length > 0 && (
                        <VStack
                            px={6}
                            py={5}
                            gap={4}
                            alignItems={'start'}
                            bg={'bg.1'}
                            borderRadius={'8px'}
                            boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                            mb={'16px'}
                            filter={isPostExpired ? 'saturate(0)' : 'none'}
                            pointerEvents={isPostExpired ? 'none' : 'auto'}
                        >
                            <Text
                                fontSize={'xl'}
                                fontWeight={'semibold'}
                                color={'text.main'}
                                opacity={isPostExpired ? 0.4 : 1}
                            >
                                {t('117')}
                            </Text>
                            {(data?.files || []).map((file) => (
                                <Link
                                    href={file.Link}
                                    target={'_blank'}
                                    fontSize={'14px'}
                                    fontWeight={'500'}
                                    opacity={isPostExpired ? 0.4 : 1}
                                >
                                    <Flex
                                        alignItems={'center'}
                                        w={'full'}
                                        gap={3}
                                        key={file.CandidateFileID}
                                        cursor={'pointer'}
                                        color={'text.secondary'}
                                    >
                                        <Image maxW={'22px'} src={getAttachmentIcon(file.Ext)} alt={file.Name} />
                                        <Tooltip
                                            label={file.Name}
                                            openDelay={500}
                                            hasArrow
                                            aria-label={file.Name}
                                            bg={'#323234'}
                                            borderRadius={'4px'}
                                            px={'16px'}
                                            py={'8px'}
                                            fontSize={'xs'}
                                            closeOnScroll
                                            shouldWrapChildren
                                        >
                                            <Text
                                                whiteSpace={'nowrap'}
                                                overflow={'hidden'}
                                                textOverflow={'ellipsis'}
                                                maxW={'290px'}
                                            >
                                                {file.Name}
                                            </Text>
                                        </Tooltip>
                                    </Flex>
                                </Link>
                            ))}
                        </VStack>
                    )}
                </Flex>
                <ReCAPTCHA
                    sitekey={RECAPTCHA}
                    ref={recaptchaRef}
                    size={'invisible'}
                    onExpired={() => {
                        recaptchaRef.current?.reset()
                    }}
                    // opacity={isPostExpired ? 0.4 : 1}
                />
            </Box>
        </>
    )
}
